import { forwardRef, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  clearable?: boolean;
  onClear?: () => void;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ leftIcon, rightIcon, clearable = false, onClear, className, value, onChange, ...props }, ref) => {
    const [internalValue, setInternalValue] = useState(value || '');
    const currentValue = value !== undefined ? value : internalValue;
    const showClear = clearable && currentValue && currentValue !== '';

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInternalValue(e.target.value);
      onChange?.(e);
    };

    const handleClear = () => {
      setInternalValue('');
      onClear?.();
      const event = { target: { value: '' } } as React.ChangeEvent<HTMLInputElement>;
      onChange?.(event);
    };

    return (
      <div className="relative flex items-center">
        {leftIcon && <div className="absolute left-3 flex items-center pointer-events-none text-text-secondary">{leftIcon}</div>}
        <input
          ref={ref}
          value={currentValue}
          onChange={handleChange}
          className={cn(
            'w-full px-4 py-3 rounded-lg',
            'bg-bg-page text-text-primary placeholder:text-text-secondary',
            'border border-border-color',
            'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary',
            'transition-all',
            leftIcon && 'pl-10',
            (showClear || rightIcon) && 'pr-10',
            className,
          )}
          {...props}
        />
        {showClear && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 flex items-center justify-center w-5 h-5 text-text-secondary hover:text-text-primary transition-colors"
            aria-label="清除"
          >
            <X size={16} />
          </button>
        )}
        {!showClear && rightIcon && <div className="absolute right-3 flex items-center">{rightIcon}</div>}
      </div>
    );
  },
);

Input.displayName = 'Input';

export default Input;
